// --------------------------------------------------------------
// Created On: 2025-08-18
// Author: Hannah Vaughan
//
// Last Modified: 2025-11-02
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useState } from "react";
import Spinner from "../Spinner/Spinner";
import DoubleConfirmModal from "../DoubleConfirmModal/DoubleConfirmModal";
import apiRequest from "src/utilities/api/apiRequest";
import { API, GATEWAY_TIMEOUT_STATUS } from "src/constants/miscellaneous";
import useReduxSelector from "../../hooks/useReduxSelector";
import getApiError from "src/utilities/api/getApiError";

// Modal for confirming if a specific asset should be migrated.
// When confirmed, this modal initiates the migration process for the asset.
export default function ConfirmMigrationModal(props: Props): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const currentUser = useReduxSelector((state) => state.currentUser);

  // Cleans up state and calls parent 'props.onCancel' function.
  function handleCancel(): void {
    setErrorMessage("");
    setLoading(false);
    props.onCancel();
  }

  // Performs an asset migration on the current asset.
  async function handleConfirm(): Promise<void> {
    setLoading(true);
    const [response] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/asset/${props.assetId}/migrate`,
      "PUT",
      null
    )) as [Response, MessageResponseBody];
    setLoading(false);

    if (response.ok) {
      setErrorMessage("");
      props.onConfirm();
    } else if (response.status === GATEWAY_TIMEOUT_STATUS) {
      setErrorMessage("Request timeout. Unable to migrate asset.");
    } else {
      setErrorMessage(await getApiError(response, "Unable to migrate asset."));
    }
  }

  return (
    <Fragment>
      <Spinner loading={loading} />
      <DoubleConfirmModal
        showModal={props.showModal}
        title="Migrate Asset?"
        content={
          "If migrated, this asset will no longer be available in its current capacity in legacy software. " +
          "Alert thresholds from legacy software will not be transferred. This action in not reversible! " +
          "Do you still want to migrate this asset?"
        }
        confirmPin="MIGRATE ASSET"
        confirmText="Migrate Asset"
        errorMessage={errorMessage}
        disabled={loading}
        onConfirm={() => handleConfirm()}
        onCancel={() => handleCancel()}
      />
    </Fragment>
  );
}

interface Props {
  showModal: boolean;
  assetId: string;
  onConfirm: () => void;
  onCancel: () => void;
}

interface MessageResponseBody {
  message: string;
}
